<template>
    <KDialog
        v-model:open="open"
        :title="dialogTitle"
        :description="dialogDescription"
        width="sm"
    >
        <div
            v-if="success"
            class="booking-dialog__success"
        >
            <Icon
                name="tabler:circle-check"
                class="booking-dialog__success-icon"
            />
            <p>
                Thanks {{ firstName }} — we've received your booking request for
                <strong>{{ booking.tour }}</strong> and will be in touch shortly.
            </p>
            <KButton
                type="button"
                label="Close"
                @click="open = false"
            />
        </div>

        <template v-else>
            <!--
                `v-show`, never `v-if`: the contact form keeps its state in its own
                `useForm()`, so unmounting it on Back would wipe every typed field, every
                touched flag and the consent box. `display: none` also takes the hidden
                step out of the tab order and the accessibility tree, so neither step
                needs `inert` bookkeeping.
            -->
            <div
                v-if="hasQuoteStep"
                v-show="onQuoteStep"
                class="booking-dialog__step"
            >
                <slot name="quote" />
            </div>

            <div
                v-show="!onQuoteStep"
                class="booking-dialog__step"
            >
                <div
                    v-if="hasQuoteStep"
                    class="booking-dialog__nav"
                >
                    <KButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        label="Back"
                        @click="step = 1"
                    >
                        <template #icon-start>
                            <Icon name="tabler:arrow-left" />
                        </template>
                    </KButton>
                    <span class="booking-dialog__progress">Step 2 of 2</span>
                </div>

                <div class="booking-dialog__summary">
                    <KLineItems
                        :items="summaryItems"
                        :total="summaryTotal"
                        muted-labels
                        aria-label="Booking summary"
                    />
                </div>

                <BookingModalForm
                    :booking="bookingSummary"
                    :send="send"
                    @started="trackStart"
                    @submitted="trackSuccess"
                    @submit-error="trackError"
                />
            </div>
        </template>

        <template
            v-if="hasQuoteStep && !success && onQuoteStep"
            #footer
        >
            <p
                v-if="blockReason"
                class="booking-dialog__block-reason"
                role="status"
            >
                {{ blockReason }}
            </p>
            <KButton
                type="button"
                :label="continueLabel"
                :disabled="!canContinue"
                @click="step = 2"
            />
        </template>
    </KDialog>
</template>

<script setup>
import { KButton, KDialog, KLineItems } from '@vemedia/karibu'
import { computed, ref, useSlots, watch } from 'vue'
import { useTracking } from '~/composables/useTracking'
import { sendRequest } from '~/utils/forms'
import BookingModalForm from './booking-modal-form.vue'

// One lead dialog for every bookable thing (tours and packages today). `itemLabel`
// names the thing in the UI; `subject` is the lead-type
// key and deliberately drives both the PostHog `form` property and the e-mail
// subject prefix so the two never diverge per call site — the tour defaults keep the
// historical strings ('tour-booking' → "Tour booking request: …") byte-identical.
//
// Passing a `quote` slot turns the dialog into two steps: configure, then leave details.
// Omitting it — which is what the tour does — leaves the single-step flow untouched.
const props = defineProps({
    booking: {
        type: Object,
        required: true,
    },
    itemLabel: {
        type: String,
        default: 'Tour',
    },
    subject: {
        type: String,
        default: 'tour-booking',
    },
    extrasLabel: {
        type: String,
        default: 'Extras',
    },
    title: {
        type: String,
        default: 'Request this booking',
    },
    description: {
        type: String,
        default: 'Leave your details and we\'ll confirm availability and finalise your booking.',
    },
    quoteTitle: {
        type: String,
        default: 'Plan this trip',
    },
    quoteDescription: {
        type: String,
        default: 'Choose your options for an instant estimate.',
    },
    continueLabel: {
        type: String,
        default: 'Continue',
    },
    /** Ignored without a `quote` slot. */
    canContinue: {
        type: Boolean,
        default: true,
    },
    /** Why `canContinue` is false, shown beside the blocked button. */
    blockReason: {
        type: String,
        default: '',
    },
    /**
     * Which step to open on. Pass 2 from a surface that already shows the quote
     * configurator, so the visitor is not asked to fill it in twice. Ignored without a
     * `quote` slot, and ignored while `canContinue` is false — there is no sense skipping
     * a step the visitor has not actually completed.
     */
    initialStep: {
        type: Number,
        default: 1,
        validator: value => value === 1 || value === 2,
    },
})

const open = defineModel('open', { type: Boolean, default: false })

const slots = useSlots()
const { capture } = useTracking()

const success = ref(false)
const firstName = ref('')

// Skipping the configurator is only honest once it is actually complete; otherwise the
// visitor would land on step two with no sight of what is missing.
const resolveInitialStep = () => (props.initialStep === 2 && props.canContinue ? 2 : 1)

// Resolved at setup as well as on open, so a dialog mounted already-open lands on the
// right step instead of waiting for the watcher's first change.
const step = ref(resolveInitialStep())

const hasQuoteStep = computed(() => Boolean(slots.quote))
const onQuoteStep = computed(() => hasQuoteStep.value && step.value === 1)

const dialogTitle = computed(() => {
    if (success.value)
        return 'Request sent!'
    return onQuoteStep.value ? props.quoteTitle : props.title
})

const dialogDescription = computed(() => {
    if (success.value)
        return ''
    return onQuoteStep.value ? props.quoteDescription : props.description
})

// The dialog now stays mounted between openings, so a second visit would otherwise reopen
// on the success panel. Reset on open rather than on close, so the exit animation does not
// flash the reset content. The contact form needs no resetting: reka unmounts the panel
// while the dialog is closed, so it remounts empty.
watch(open, (isOpen) => {
    if (!isOpen)
        return

    step.value = resolveInitialStep()
    success.value = false
    firstName.value = ''
})

// Call sites keep their Storyblok-shaped, snake_case summary; the typed camelCase
// BookingSummary the form layer works against is adapted here, at the boundary.
const bookingSummary = computed(() => ({
    kind: props.subject === 'package-booking' ? 'package-booking' : 'tour-booking',
    itemUuid: props.booking.item_uuid,
    tour: props.booking.tour,
    guests: props.booking.guests,
    guestBreakdown: props.booking.guest_breakdown,
    guestsText: props.booking.guests_text,
    travelDate: props.booking.travel_date,
    transferText: props.booking.transfer_text,
    extrasText: props.booking.extras_text,
    total: props.booking.total,
    pageUrl: props.booking.page_url,
}))

const send = payload => sendRequest(payload)

const summaryItems = computed(() => [
    { label: props.itemLabel, value: props.booking.tour },
    { label: 'Guests', value: props.booking.guests_text },
    props.booking.travel_date && { label: 'Travel date', value: props.booking.travel_date },
    props.booking.transfer_text && { label: 'Transfer', value: props.booking.transfer_text },
    ...(props.booking.summary_options?.length
        ? props.booking.summary_options
        : [props.booking.extras_text && { label: props.extrasLabel, value: props.booking.extras_text }]),
].filter(Boolean))

const summaryTotal = computed(() => ({
    label: 'Estimated total',
    value: `$${props.booking.total}`,
}))

function trackStart() {
    capture('lead_started', { form: props.subject, tour: props.booking.tour })
}

function trackSuccess(payload) {
    success.value = true
    firstName.value = payload.contact.name.split(' ')[0]
    capture('lead_submitted', { form: props.subject, tour: props.booking.tour, total: props.booking.total })
    capture('booking_submitted', {
        channel: 'email',
        tour: props.booking.tour,
        total: props.booking.total,
        guests: props.booking.guests,
    })
}

function trackError() {
    capture('lead_submit_error', { form: props.subject, tour: props.booking.tour })
}
</script>

<style lang="scss">
// Unscoped on purpose. Slot content passed into KDialog keeps this component's scope id,
// but KDialog's own panel and overlay do not, so a scoped rule aimed at them would
// silently do nothing. Everything below is nested under one block instead.
.booking-dialog {
    &__step {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
    }

    &__nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
    }

    &__progress {
        color: var(--color-text-muted);
        font-size: var(--font-size-1);
    }

    &__block-reason {
        flex: 1 1 12rem;
        margin: 0;
        color: var(--color-danger-text);
        font-size: var(--font-size-1);
        text-align: start;
    }

    &__summary {
        padding: var(--space-4);
        border-radius: var(--radius-4);
        background: var(--color-bg-subtle);
    }

    // `booking-modal-form.vue` has no stylesheet of its own; these are its classes.
    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
    }

    &__error {
        margin: 0;
        color: var(--color-danger-text);
        font-size: var(--font-size-2);
    }

    &__success {
        text-align: center;

        &-icon {
            margin-block-end: var(--space-2);
            color: var(--color-success-solid);
            font-size: 3rem;
        }

        p {
            margin-block: var(--space-2) var(--space-5);
        }
    }
}
</style>
